import { useTranslation } from '@jobcost-monorepo/i18n';
import {
  CurrencyInputForm,
  DatePicker,
  Dropdown,
  HoursInputForm,
  InputForm,
  SwitchForm,
  TextAreaForm,
  TriState,
} from '@jobcost-monorepo/ui';
import {
  FormFieldOption,
  GridFilterOption,
  IBaseModel,
  IListFetchParams,
  Paths,
} from '../../common';
import { enumToKeyValueArray } from './helperEnum';
import { cn } from '@jobcost-monorepo/utils';
import DropdownCrud from '../../ui/DropdownCrud';
import React from 'react';

/**
 * FormFieldRenderer
 * Function that return the correct form field based on the filter type
 * @param field - indica le proprietà del campo da utilizzare per renderizzare il componente adatto
 * @param isGridForm - identifica se è un campo del form o dei filtri
 */
const FormFieldRenderer = <T extends IBaseModel, Q extends IListFetchParams>(
  field: GridFilterOption<Q> | FormFieldOption<T>,
  isGridForm = true,
) => {
  const { linkedField, formatType } = field;

  if (field.formatType === 'custom') {
    const customComponent =
      field.formatType === 'custom' ? field.customComponent : undefined;
    if (customComponent) {
      const Component = customComponent;
      return <Component />;
    }
    return <div />;
  }

  const linkedFieldForDefaultValue = field.linkedFieldForDefaultValue;
  const enumValues = field.enumValues;
  const translationModuleName = field.translationModuleName;
  const moduleName = field.moduleName;
  const width = field.width;
  const dependsOnFields = (field as any).dependsOnFields;
  const dependsOnFieldsForSearch = (field as any).dependsOnFieldsForSearch;
  const dependsOnMappedFieldsForSearch = (field as any)
    .dependsOnMappedFieldsForSearch;
  const dependsOnForcedFieldsForSearch = (field as any)
    .dependsOnForcedFieldsForSearch;
  const type = (field as any).type ?? 'text';
  const readOnly = (field as any).readOnly ?? false;
  const checkIfDisabled = (field as any).checkIfDisabled ?? undefined;

  const { t } = useTranslation(translationModuleName?.toLocaleLowerCase());
  const label = isGridForm
    ? t(`form.${linkedField}`)
    : t(`filter.${linkedField}`);

  switch (formatType) {
    case 'input':
      return (
        <InputForm
          name={linkedField}
          errorName={linkedField}
          label={label}
          type={type}
          disabled={checkIfDisabled ? checkIfDisabled() : readOnly}
          width={width}
        />
      );
    case 'hours':
      return (
        <HoursInputForm
          name={linkedField}
          errorName={linkedField}
          label={label}
          type={type}
          disabled={checkIfDisabled ? checkIfDisabled() : readOnly}
          width={width}
        />
      );
    case 'currency':
      return (
        <CurrencyInputForm
          name={linkedField}
          errorName={linkedField}
          label={label}
          isCurrency
          disabled={checkIfDisabled ? checkIfDisabled() : readOnly}
          width={width}
        />
      );
    case 'textArea':
      return (
        <TextAreaForm
          name={linkedField}
          errorName={linkedField}
          label={label}
          readOnly={readOnly}
          inputWidth={width}
          disabled={checkIfDisabled ? checkIfDisabled() : readOnly}
        />
      );

    case 'dropdown':
      return enumValues ? (
        <Dropdown
          name={linkedField}
          errorName={linkedField}
          label={label}
          items={enumToKeyValueArray(
            enumValues,
            linkedField,
            translationModuleName,
          )}
          searchable
          disabled={checkIfDisabled ? checkIfDisabled() : readOnly}
          width={width}
        />
      ) : (
        <div />
      );

    case 'dropdownWithSearch':
      return (
        <DropdownCrud<T, Q>
          moduleName={moduleName}
          linkedField={linkedField as Paths<T>}
          linkedFieldForDefaultValue={linkedFieldForDefaultValue as Paths<T>}
          label={label}
          width={width}
          dependsOnFields={dependsOnFields}
          dependsOnFieldsForSearch={dependsOnFieldsForSearch}
          dependsOnMappedFieldsForSearch={dependsOnMappedFieldsForSearch}
          dependsOnForcedFieldsForSearch={dependsOnForcedFieldsForSearch}
          forceDisabled={checkIfDisabled ? checkIfDisabled() : readOnly}
        />
      );

    case 'date':
      return (
        <DatePicker
          name={linkedField}
          errorName={linkedField}
          label={label}
          disabled={checkIfDisabled ? checkIfDisabled() : readOnly}
          inputWidth={width}
        />
      );

    case 'boolean':
      return (
        <SwitchForm
          id={linkedField}
          name={linkedField}
          label={label}
          disabled={checkIfDisabled ? checkIfDisabled() : readOnly}
          width={width}
        />
      );

    case 'tristate':
      return <TriState name={linkedField} label={label} />;

    default:
      return <div />;
  }
};

/**
 * Function that generates the filter form based on the filters
 * @param filters - Filters to be rendered
 */
export const generateFilterForm = <
  T extends IBaseModel,
  Q extends IListFetchParams,
>(
  filters: Array<GridFilterOption<Q>>,
) => {
  // eslint-disable-next-line prefer-const
  let rows: Record<number, GridFilterOption<Q>[]> = {};

  // Group filters by row
  for (const item of filters) {
    const { y } = item.position;
    if (!rows[y]) {
      rows[y] = [];
    }
    rows[y] = [...rows[y], item];
  }

  // Loop through rows and render filters in each row
  return Object.keys(rows).map((row) => (
    <div key={row} className="flex flex-row space-x-2">
      {rows[Number(row)].map((filter) => (
        <div key={filter.linkedField} className={filter.width}>
          {FormFieldRenderer<T, Q>(filter, false)}
        </div>
      ))}
    </div>
  ));
};

/**
 * Function that generates the grid form based on the form grid fields
 * @param formFieldsOptions
 */
export const generateGridForm = <
  T extends IBaseModel,
  Q extends IListFetchParams,
>(
  formFieldsOptions: Array<FormFieldOption<T>>,
) => {
  // eslint-disable-next-line prefer-const
  let rows: Record<number, FormFieldOption<T>[]> = {};

  // Group filters by row
  for (const item of formFieldsOptions) {
    if (item.visible) {
      const { y } = item.position;
      if (!rows[y]) {
        rows[y] = [];
      }
      rows[y] = [...rows[y], item];
    }
  }

  // Loop through rows and render filters in each row
  return Object.keys(rows).map((row) => (
    <div key={row} className="flex flex-row w-full flex-1 space-x-2">
      {rows[Number(row)].map((field) => {
        const fieldWidth =
          field.formatType === 'custom' ? undefined : field.width;
        return (
          <div
            key={field.linkedField}
            className={cn('flex flex-1', fieldWidth)}
          >
            {FormFieldRenderer<T, Q>(field)}
          </div>
        );
      })}
    </div>
  ));
};
